/// <amd-module name="Test/ManageTestPlans/Actions/TestPlanDirectoryActionsCreator" />
import { Favorite } from "Favorites/Common/Contracts/Favorite";
import { TestPlanDirectoryActionsHub } from "Test/ManageTestPlans/Actions/TestPlanDirectoryActionsHub";
import { IPivotFilterState } from "Test/ManageTestPlans/Contracts";
import { TestPlanDirectorySource } from "Test/ManageTestPlans/Sources/TestPlanDirectorySource";
import { TestPlanFavoritesSource } from "Test/ManageTestPlans/Sources/TestPlanFavoritesSource";
import { TestPlanDirectoryStore } from "Test/ManageTestPlans/Stores/TestPlanDirectoryStore";
import { TcmPerfScenarios, TelemetryConstants } from "Test/ManageTestPlansCommon/Constants";
import {
    IAllTestPlanPayload,
    IAllTestPlanInitialPayload,
    IMyFavoriteTestPlanPayload,
    IMyTestPlanPayload,
    IMyTestPlanSkinnyPayload,
    ITeamTestPlanData,
    ITestPlan,
    ITestPlanDirectoryListComponentState,
    ITestPlanRow,
} from "Test/ManageTestPlansCommon/Contracts";
import {
    DirectoryPivotType,
    FavoriteState
} from "Test/ManageTestPlansCommon/Constants"
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPerformanceService, IVssScenario } from "VSS/Platform/Performance";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { IFilterState } from "VSSUI/Utilities/Filter";
import { IGlobalMessagesService, IToast } from "TFS/GlobalBanner/GlobalMessagesService";
import * as Resources from "Test/ManageTestPlans/Resources";

export class TestPlanDirectoryActionsCreator {

    public static getInstance(): TestPlanDirectoryActionsCreator {
        if (!TestPlanDirectoryActionsCreator._instance) {
            TestPlanDirectoryActionsCreator._instance = new TestPlanDirectoryActionsCreator(
                TestPlanDirectoryActionsHub.getInstance(),
                TestPlanDirectorySource.getInstance(),
                TestPlanFavoritesSource.getInstance());
        }
        return TestPlanDirectoryActionsCreator._instance;
    }

    public init(context: IVssPageContext) {
        this._pageContext = context;
        TestPlanDirectorySource.getInstance().init(context);
        TestPlanFavoritesSource.getInstance().init(context);
    }


    constructor(actionsHub: TestPlanDirectoryActionsHub,
        source: TestPlanDirectorySource,
        favoritesSource: TestPlanFavoritesSource) {
        this._actionsHub = actionsHub;
        this._source = source;
        this._favoritesSource = favoritesSource;
        this._isFirstBatch = true;
    }

    /**
 * fetch and invoke dataAvailable action
 * 1. fetch page data for mine pivot
 * 2. fetch test plan work item details
 */
    public initializeMineTestPlan(): void {
        if (this._isMyTestPlanSkinnyProviderEnabled()) {
            this._source.getMyFavoriteTestPlanData().then((pageData: IMyFavoriteTestPlanPayload) => {
                this._actionsHub.myFavoriteTestPlanDataAvailableAction.invoke(pageData);
                this._source.getMySkinnyTestPlanData().then((myTestPlans: IMyTestPlanSkinnyPayload) => {
                    this._actionsHub.mySkinnyTestPlanPageDataAvailableAction.invoke(myTestPlans);
                    this._loadTestPlanMetaData(this._getTestplanIdsFromTeams(myTestPlans.teams));
                }, this._errorHandler);

            }, this._errorHandler);
        }
        else {
            this._source.getMyTestPlanPageData().then((pageData: IMyTestPlanPayload) => {
                this._publishTelemetry(DirectoryPivotType.mine, pageData.teams, pageData.favorites.length);
                this._actionsHub.myTestPlanPageDataAvailableAction.invoke(pageData);
                this._loadTestPlanMetaData(this._getTestplanIdsFromTeams(pageData.teams));
            }, this._errorHandler);
        }
    }

    /**
    * fetch and invoke dataAvailable action
    * 1. fetch page data for all pivot
    * 2. fetch test plan work item details
    */
    public initializeAllTestPlan(): void {

        //show toast to indicate start of the loading of all test plans in current project.

        let globalMessagingService: IGlobalMessagesService = this._pageContext.getService("IGlobalMessagesService");
        let toast: IToast = {
            message: Resources.LoadingAllTestPlans,
            duration: 3000
        }
        globalMessagingService.addToast(toast);

        if (this._isAllTestPlanSkinnyProviderEnabled()) {
            this._source.getAllTestPlanInitialPageData().then((initialPageData: IAllTestPlanInitialPayload) => {
                this._publishTelemetry(DirectoryPivotType.all, initialPageData.teams);
                this._actionsHub.allTestPlanPageInitialDataAvailableAction.invoke(initialPageData);
                this._loadTestPlanMetaData(this._getTestplanIdsFromTeams(initialPageData.teams));
                this._internalGetAllTestPlanPageData();
            }, this._errorHandler);
        }
        else {
            this._internalGetAllTestPlanPageData();
        }

        this.refreshFavorites();
    }

    private _internalGetAllTestPlanPageData() {
        this._source.getAllTestPlanPageData().then((pageData: IAllTestPlanPayload) => {
            this._publishTelemetry(DirectoryPivotType.all, pageData.teams);
            this._actionsHub.allTestPlanPageDataAvailableAction.invoke(pageData);
            this._loadTestPlanMetaData(this._getTestplanIdsFromTeams(pageData.teams), true);
            // 19999 is the maximum limit WIQL can return, thus if we get 19999 items back, 
            //  it is likely that some test plans could not be retrieved from WIQL.
            const TestPlansWIQLLoadLimit = 19999;
            if (pageData.teams && pageData.teams[0].testPlans.length >= TestPlansWIQLLoadLimit) {
                const globalMessagingService: IGlobalMessagesService = this._pageContext.getService("IGlobalMessagesService");
                const toast: IToast = {
                    message: Resources.TestplanLimitMessage,
                    duration: 3000
                };
                globalMessagingService.addToast(toast);
            }
        }, this._errorHandler);
    }

    public refreshFavorites() {
        this._favoritesSource.getFavorites(true).then(
            (favorites: Favorite[]) => {
                this._actionsHub.favoritesDataAvailableAction.invoke(favorites);
            }, this._errorHandler);
    }

    /**
 * Filter boards in directory view for a given pivot.
 * @param pivot
 * @param filter
 */
    public filterPivotItems(pivot: DirectoryPivotType, filter: IFilterState): void {

        const pivotFilterState: IPivotFilterState = {
            filterState: filter,
            pivot: pivot
        };

        this._actionsHub.pivotFilterChanged.invoke(pivotFilterState);
    }

    /**
   * Pivot is switched
   * initialte all/mine data fetching for first time only
   * @param key
   */
    public pivotSwitched(key: string): void {
        this._actionsHub.pivotSwitched.invoke(key);

        let state: ITestPlanDirectoryListComponentState;
        switch (key) {

            case DirectoryPivotType.all:
                state = TestPlanDirectoryStore.getInstance().getAllTestPlanState();
                if (state.isLoading) {
                    this.initializeAllTestPlan();
                }           
               break;

            case DirectoryPivotType.mine:
                state = TestPlanDirectoryStore.getInstance().getMineTestPlanState();
                if (state.isLoading) {
                    this.initializeMineTestPlan();
                }
                break;

        }
    }

    public patchPlan(row: ITestPlanRow): void {
        this._actionsHub.planPatched.invoke(row);
    }

    public deletePlan(testPlanId: number): void {
        this._actionsHub.planDeleted.invoke(testPlanId);
    }

    public changeColumnSort(columnName: string): void {
        this._actionsHub.changeColumnSortingAction.invoke(columnName);
    }

    public expandGroupRow(groupId: string): void {
        this._actionsHub.expandGroupRow.invoke(groupId);
    }

    /**
     * Collapse group row
     * @param groupId
     */
    public collapseGroupRow(groupId: string): void {
        this._actionsHub.collapseGroupRow.invoke(groupId);
    }

    /**
  * Invokes add to favorites action
  * @param item testplan directory row-item
  */
    public favoriteTeamTestPlan(item: ITestPlanRow): void {
        // Avoid favoriting the item multiple times. The favorites service allows favoriting
        // the same artifact again, in which case we need to unfavorite multiple times to
        // remove all the favorites
        if (!this._isRowSaving(item)) {
            this._actionsHub.beginAddToFavorites.invoke(item);
            this._favoritesSource.createFavorite(item.title, item.testPlanId.toString()).then(
                (favorite: Favorite) => {
                    this._actionsHub.endAddToFavorites.invoke(favorite);
                },
                (error: Error) => {
                    this.showErrorMessage(error);
                    this._actionsHub.addToFavoritesFailed.invoke(item);
                }
            );
        }

    }

    /**
     * Invokes remove from favorites action
     * @param item testplan directory row-item
     */
    public unfavoriteTeamTestPlan(item: ITestPlanRow): void {
        // Avoid favoriting the item multiple times. The favorites service allows favoriting
        // the same artifact again, in which case we need to unfavorite multiple times to
        // remove all the favorites
        if (!this._isRowSaving(item)) {
            this._actionsHub.beginRemoveFromFavorites.invoke(item);
            this._favoritesSource.deleteFavorite(item.favoriteId).then(
                () => {
                    this._actionsHub.endRemoveFromFavorites.invoke(item);
                },
                (error: Error) => {
                    this.showErrorMessage(error);
                    this._actionsHub.removeFromFavoritesFailed.invoke(item);
                }
            );
        }
    }

    /**
     * 
     * @param ids 
     * @param isInternalGetAllPlans Used to indicate the internal call to load all test plans and 
     * used as indicator to differentiate from loading the initial data. This helps to determine if the "Loading Completed" message can be shown
     */
    private _loadTestPlanMetaData(ids: number[], isInternalGetAllPlans: boolean = false): void {
        const idsToFetch = ids.splice(0, this.batchSize);

        if (idsToFetch.length > 0) {

            if (this._isFirstBatch) {
                // Start measuring time taken to fetch metadata for the 1st batch of test plans
                this._scenario = this._perfService.startScenario(TcmPerfScenarios.FetchTestPlansMetadata, false);
                this._scenario.properties["idsCount"] = idsToFetch.length;
            }

            this._source.getTestPlanMetaData(idsToFetch).then((testPlanMap: { [key: string]: ITestPlan }) => {

                if (this._isFirstBatch) {
                    // Stop measuring time taken to fetch metadata for the 1st batch
                    this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.FetchTestPlansMetadata, false);
                    this._isFirstBatch = false;
                }

                this._actionsHub.testPlanMetaDataAvailableAction.invoke(testPlanMap);
                this._loadTestPlanMetaData(ids, isInternalGetAllPlans);
            },
                (error: Error) => {
                    this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.FetchTestPlansMetadata, false, undefined, { status: "error" });
                });
        }
        else {
            if (isInternalGetAllPlans) {
                const globalMessagingService: IGlobalMessagesService = this._pageContext.getService("IGlobalMessagesService");
                const toast: IToast = {
                    message: Resources.LoadingCompleted,
                    duration: 3000
                }
                globalMessagingService.addToast(toast);
            }

            // in case of no test plans passing empty map so as to set loading state to false.
            this._actionsHub.testPlanMetaDataAvailableAction.invoke({});
        }
    }

    private _getTestplanIdsFromTeams(teams: ITeamTestPlanData[]): number[] {
        let idsToFetch: number[] = [];
        for (const team of teams) {
            const testPlans = team.testPlans;
            for (const testPlan of testPlans) {
                const testPlanId = testPlan.id;
                if (!isNaN(testPlanId)) {
                    idsToFetch.push(testPlanId);
                }
            }
        }

        return idsToFetch;
    }

    private _isMyTestPlanSkinnyProviderEnabled(): boolean {
        return isFeatureFlagEnabled(this._pageContext, c_mineTestPlanSkinnyProviderFF, false)
    }

    private _isAllTestPlanSkinnyProviderEnabled(): boolean {
        return isFeatureFlagEnabled(this._pageContext, c_allTestPlanSkinnyProviderFF, false)
    }

    public showErrorMessage(error: Error): void {
        this._actionsHub.showErrorMessage.invoke(error);
    }

    private _publishTelemetry(tab: string, teams: ITeamTestPlanData[], favorites?: number) {
        let count = 0;
        teams.forEach(team => {
            count += team.testPlans.length;
        });

        this._telemetryService.publishEvent(TelemetryConstants.areaTestManagement, TelemetryConstants.featureNewTestPlan_Payload, {
            "Tab": tab,
            "TestPlansCount": count,
            "TeamsCount": teams.length,
            "Favorites": favorites
        });
    }

    private _myTestPlanPromise: Promise<any>;
    private _actionsHub: TestPlanDirectoryActionsHub;
    private _source: TestPlanDirectorySource;
    private _favoritesSource: TestPlanFavoritesSource;
    private _pageContext: IVssPageContext;
    private _isFirstBatch: boolean;
    private static _instance: TestPlanDirectoryActionsCreator;
    private readonly batchSize: number = 200;
    private _scenario: IVssScenario;

    private get _perfService() {
        return this._pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
    }

    private get _telemetryService() {
        return this._pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    }


    private _isRowSaving(item: ITestPlanRow): boolean {
        return item.favoriteState === FavoriteState.Favoriting ||
            item.favoriteState === FavoriteState.Unfavoriting;
    }

    private _errorHandler = (error: Error) => {
        this.showErrorMessage(error);
    };

}

const c_mineTestPlanSkinnyProviderFF = "WebAccess.TestManagement.MineTestPlanSkinnyProvider";
const c_allTestPlanSkinnyProviderFF = "WebAccess.TestManagement.AllTestPlanSkinnyProvider";
